import { Box, Typography } from "@mui/material";
import type { CognimeterScale } from "./Cognimeter";
import CognimeterReferenceLegend from "./CognimeterReferenceLegend";

// Легенда цветов под когниметром (Andrey K., сент. 2026): текст + прямоугольник
// с градиентом того цвета, о котором речь. Жёлтый идёт от зеленоватого слева
// к красноватому справа, чтобы показать спектр. Набор пунктов зависит от шкалы.

// Цвета из STOPS когниметра
const GREEN_A = "rgb(30,160,72)", GREEN_B = "rgb(64,174,64)";
const YEL_A = "rgb(176,198,38)", YEL_B = "rgb(246,208,18)", YEL_C = "rgb(250,138,12)";
const RED_A = "rgb(230,60,26)", RED_B = "rgb(200,18,26)";
const GRAY = "#98a3b1";

const GREEN_SWATCH = `linear-gradient(90deg, ${GREEN_A}, ${GREEN_B})`;
const YELLOW_SWATCH = `linear-gradient(90deg, ${YEL_A}, ${YEL_B} 45%, ${YEL_C})`;
const RED_SWATCH = `linear-gradient(90deg, ${RED_A}, ${RED_B})`;
const FULL_SWATCH = `linear-gradient(90deg, ${GREEN_A}, ${GREEN_B} 48%, ${YEL_A} 60%, ${YEL_B} 76%, ${YEL_C} 87%, ${RED_A} 93%, ${RED_B})`;
const GRAY_SWATCH = `linear-gradient(90deg, ${GRAY}55, ${GRAY}, ${GRAY}55)`;

export type CognimeterLegendLayout = "below" | "side";
export const COGNIMETER_LEGEND_LAYOUTS: { value: CognimeterLegendLayout; label: string }[] = [
  { value: "below", label: "Below the scale" },
  { value: "side", label: "Vertical, beside the scale" },
];

interface Entry { swatch: string; color: string; text: string }

const DETAILED_ENTRIES: Entry[] = [
  { swatch: GREEN_SWATCH, color: "Green", text: "Normal-performance reference range" },
  { swatch: GRAY_SWATCH, color: "Gray zone", text: "borderline (scores 33–46)" },
  { swatch: YELLOW_SWATCH, color: "Yellow", text: "Mild-impairment reference range" },
  { swatch: RED_SWATCH, color: "Red", text: "Severe-impairment reference range" },
];

const ENTRIES: Record<Exclude<CognimeterScale, "gradient" | "reference">, Entry[]> = {
  "three-zone": DETAILED_ENTRIES,
  "two-zone": DETAILED_ENTRIES,
};

function Swatch({ background, width = 68 }: { background: string; width?: number }) {
  return (
    <Box
      sx={{
        width, height: 20, flex: "none", borderRadius: 1,
        background, border: "1px solid rgba(0,0,0,0.12)",
      }}
    />
  );
}

// Вертикальная легенда сбоку от шкалы: сплошной столбик цветов сверху (NC)
// вниз (Dementia), серая зона — тонкий сегмент между NC и MCI, как на шкале.
const V = (g: string) => g.replace("90deg", "180deg");
const SIDE_LEGEND_HEIGHT = 228;

function VerticalLegend({ scale }: { scale: CognimeterScale }) {
  const segments: { bg: string; h: number; color?: string; text: string }[] =
    scale === "gradient"
      ? [{ bg: V(FULL_SWATCH), h: SIDE_LEGEND_HEIGHT, text: "" }]
      : [
          { bg: V(GREEN_SWATCH), h: 82, color: "Green", text: "Normal-performance reference range" },
          { bg: V(GRAY_SWATCH), h: 32, color: "Gray zone", text: "borderline (scores 33–46)" },
          { bg: V(YELLOW_SWATCH), h: 75, color: "Yellow", text: "Mild-impairment reference range" },
          { bg: V(RED_SWATCH), h: 39, color: "Red", text: "Severe-impairment reference range" },
        ];

  return (
    <Box
      sx={{
        py: 0.9, px: 1, bgcolor: "#f5f8fc", border: "1px solid #dbe3ee", borderRadius: 1.5,
        color: "#26344a", fontSize: 13, lineHeight: 1.25, width: 210, flex: "none",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>Legend</Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Box sx={{ width: 16, flex: "none", borderRadius: 0.8, overflow: "hidden", border: "1px solid rgba(0,0,0,0.12)" }}>
          {segments.map((sg, i) => (
            <Box key={i} sx={{ height: sg.h, background: sg.bg }} />
          ))}
        </Box>
        {scale === "gradient" ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: SIDE_LEGEND_HEIGHT }}>
            <span><b>Green</b> — Normal-performance reference range</span>
            <span><b>Yellow</b> — Mild-impairment reference range</span>
            <span><b>Red</b> — Severe-impairment reference range</span>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {segments.map((sg, i) => (
              <Box key={i} sx={{ height: sg.h, display: "flex", alignItems: "center" }}>
                <span><b>{sg.color}</b> — {sg.text}</span>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function CognimeterLegend({
  scale, layout = "below",
}: { scale: CognimeterScale; layout?: CognimeterLegendLayout }) {
  if (scale === "reference") return <CognimeterReferenceLegend layout={layout} />;
  if (layout === "side") return <VerticalLegend scale={scale} />;

  const wrap = {
    mt: 2, mb: 0, py: 1.6, px: 3,
    bgcolor: "#f5f8fc", border: "1px solid #dbe3ee", borderRadius: 2,
    color: "#26344a", fontSize: 18, lineHeight: 1.5,
  };

  if (scale === "gradient") {
    return (
      <Box sx={wrap}>
        <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 1 }}>Legend</Typography>
        <Box sx={{ height: 22, borderRadius: 1, background: FULL_SWATCH, border: "1px solid rgba(0,0,0,0.12)" }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.6, fontSize: 16, fontWeight: 600, gap: 1 }}>
          <span>Green — Normal-performance reference range</span>
          <span style={{ textAlign: "center" }}>Yellow — Mild-impairment reference range</span>
          <span style={{ textAlign: "right" }}>Red — Severe-impairment reference range</span>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={wrap}>
      <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 1 }}>Legend</Typography>
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 0.8 }}>
        {ENTRIES[scale].map((e) => (
          <Box component="li" key={e.color} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Swatch background={e.swatch} />
            <span><b>{e.color}</b> — {e.text}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
